import clsx from 'clsx';
import { useState } from 'react';

interface Props {
  src?: string | null;
  name?: string | null;
  /** Avatar diameter in pixels. */
  size?: number;
  /** Extra class names for the wrapper. */
  className?: string;
  /** Optional alt text override (defaults to name). */
  alt?: string;
}

/**
 * Avatar — falls back to a deterministic gradient + first-letter monogram
 * when no profile image is set (or when the image fails to load).
 *
 * The gradient is derived from a stable hash of the user's name so the
 * same person always gets the same color, but different users get
 * different ones.
 */
export function Avatar({ src, name, size = 32, className, alt }: Props) {
  const [broken, setBroken] = useState(false);
  const safeName = (name || '?').trim() || '?';
  const initial = safeName[0].toUpperCase();

  const showImage = !!src && !broken;
  const fontSize = Math.max(10, Math.round(size * 0.42));

  return (
    <div
      className={clsx('rounded-full overflow-hidden flex items-center justify-center shrink-0 select-none', className)}
      style={{
        width: size,
        height: size,
        background: showImage ? undefined : monogramGradient(safeName),
        boxShadow: showImage ? undefined : 'inset 0 0 0 1px rgba(255,255,255,0.08)',
      }}
      aria-label={alt || safeName}
    >
      {showImage ? (
        <img
          src={src!}
          alt={alt || safeName}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          draggable={false}
        />
      ) : (
        <span
          style={{
            fontFamily: '"Manrope", sans-serif',
            fontSize,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.94)',
            textShadow: '0 1px 2px rgba(0,0,0,0.35)',
            lineHeight: 1,
          }}
        >{initial}</span>
      )}
    </div>
  );
}

/**
 * Pick two deterministic colors from a curated palette based on the name
 * and return a CSS gradient string. The palette is tuned to harmonize
 * with the felt-green app theme.
 */
function monogramGradient(name: string): string {
  const palette: [string, string][] = [
    ['#2a5b4c', '#0f2a20'], // forest
    ['#365574', '#152537'], // navy
    ['#7c4a2b', '#3a1d10'], // copper
    ['#5a3a78', '#241236'], // plum
    ['#8a6b1c', '#3a2a08'], // amber
    ['#7a3147', '#3a0f1c'], // wine
    ['#3a5e7a', '#0f2238'], // steel-blue
    ['#4a6b2a', '#1a2a0e'], // moss
    ['#26615e', '#0a2625'], // teal
    ['#7a4b8a', '#2c1639'], // orchid
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  const [from, to] = palette[h % palette.length];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}
